import React from "react";
import { ApolloClient, InMemoryCache } from "@apollo/client/core";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import { createHttpLink, HttpOptions } from "@apollo/client/link/http";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { onError } from "@apollo/client/link/error";
import { TASK_ID, UPDATE_ID } from "../../graphql/constants/type-id-fields";
import {
  USER_TASK_SUMMARY,
  COMMUNITY_TASK_SUMMARY,
  USER_TASK_DETAILS,
  USER_TRACKED_TASK_DETAILS,
  DRAFT_USER_TASK_DETAILS,
} from "../../graphql/constants/type-names";
import { transformCommunityTasksSummaryResponse } from "../../components/community/helpers/process-summary-response";
import {
  completeUserLogout,
  getTokenForApiRequest,
} from "../../utils/helpers/auth-helper";
import { OPERATIONS_IGNORE_TOKEN_CHECK } from "../../utils/constants/login";
import { ROOT_GET_COMMUNITY_TASKS_SUMMARY } from "../../graphql/constants/type-root-fields";
import { hasAuthenticationError } from "../../utils/helpers/query-error-processing";
import { generateSha256Hash } from "../../utils/helpers/generate-sha-hash";
import ErrorBoundary from "../../molecules/error-boundary/ErrorBoundary";

function getAuthLink(ssrMode: boolean) {
  return setContext(async (operation, { headers }) => {
    let token;
    // Skip token check for server env and for select operations
    if (
      !ssrMode &&
      !OPERATIONS_IGNORE_TOKEN_CHECK.has(operation?.operationName || "")
    ) {
      token = await getTokenForApiRequest(operation?.operationName);
    }

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });
}

function getErrorLink(client: ApolloClient<any>) {
  return onError(({ operation, graphQLErrors }) => {
    const { response } = operation.getContext() || {};
    const hasAuthError = hasAuthenticationError({ graphQLErrors });

    // Unauthorized, redirect to login page
    if (response?.status === 401 || hasAuthError) {
      completeUserLogout(client, { autoLogout: true });
    }

    // TODO add a logger
  });
}

type OnAppInitLoadParams = {
  ssrMode: boolean;
  fetchForClient?: HttpOptions["fetch"];
};

type AppRendererProps = {
  element: JSX.Element;
};

export default function onAppInitLoad({
  ssrMode,
  fetchForClient,
}: OnAppInitLoadParams) {
  const httpLink = createHttpLink({
    uri: process.env.GRAPH_QL_URI,
    fetch: fetchForClient,
    credentials: "include",
  });
  const persistedQueryLink = createPersistedQueryLink({
    sha256: generateSha256Hash,
  });

  const client = new ApolloClient({
    ssrMode,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            [ROOT_GET_COMMUNITY_TASKS_SUMMARY]: {
              keyArgs: false,
              merge: transformCommunityTasksSummaryResponse,
            },
          },
        },
        [USER_TASK_SUMMARY]: {
          keyFields: [TASK_ID],
        },
        [USER_TASK_DETAILS]: {
          keyFields: [TASK_ID],
        },
        [DRAFT_USER_TASK_DETAILS]: {
          keyFields: [TASK_ID],
          // Method to modify the incoming data to the internal format
          fields: {
            accepted: {
              read: (existing) => existing ?? false,
            },
          },
        },
        [COMMUNITY_TASK_SUMMARY]: {
          keyFields: [TASK_ID],
        },
        [USER_TRACKED_TASK_DETAILS]: {
          keyFields: [UPDATE_ID],
        },
      },
    }),
  });

  const authLink = getAuthLink(ssrMode);
  const errorLink = getErrorLink(client);
  client.setLink(
    errorLink.concat(authLink.concat(persistedQueryLink.concat(httpLink)))
  );

  // IMP - this is called twice now since moving to the Gatsby head API
  // Apparently they couldn't move the head in the root
  return function AppRenderer({ element }: AppRendererProps) {
    return (
      <ApolloProvider client={client}>
        <ErrorBoundary>{element}</ErrorBoundary>
      </ApolloProvider>
    );
  };
}
